import React, { useState, useEffect, useMemo } from 'react';
import ProtectedRoute from '../../routes/protected';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import { Editor } from 'primereact/editor';
import { renderHeaderEditor } from '../../helpers/editorToolbar';
import { isRichTextEmpty } from '../../helpers/richText';
import { onSurveyDescriptionEditorLoad } from '../../helpers/surveyDescriptionEditor';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import surveyService from '../../services/data/survey.service';
import SurveyPageHeader from './SurveyPageHeader';

export default function SurveyTemplateCreate() {
    const navigate = useNavigate();
    const toast = React.useRef(null);
    const VerifyPerms = UsePerms();
    const { rootUser, company } = useCompanyData();
    const companyId = localStorage.getItem('companyId');
    const canCreateTemplate = VerifyPerms(['add_surveytemplate']) || rootUser;
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: '',
        description: '',
    });
    const headerEditor = useMemo(() => renderHeaderEditor(), []);

    useEffect(() => {
        if (company?.id && !canCreateTemplate) {
            navigate('/survey/template/list/');
            return;
        }
    }, [company?.id, canCreateTemplate, navigate]);

    function handleSubmit(e) {
        e.preventDefault();
        if (!form.name) {
            if (toast.current) toast.current.show({ severity: 'warn', summary: 'Atenção', detail: 'Preencha o nome do template' });
            return;
        }
        setSubmitting(true);
        surveyService.createTemplate({
            company_id: companyId,
            name: form.name,
            description: isRichTextEmpty(form.description) ? '' : form.description,
        })
            .then((data) => {
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: 'Template criado. Adicione as perguntas.',
                    });
                }
                if (data?.id) {
                    navigate(`/survey/template/${data.id}/`, { state: { openAddQuestion: true } });
                } else {
                    navigate('/survey/template/list/');
                }
            })
            .catch(err => {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: err.response?.data?.detail || 'Falha ao criar' });
            })
            .finally(() => setSubmitting(false));
    }

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid">
                <SurveyPageHeader title="Novo Template" />
                <div className="my-4 mx-5 py-3 rounded-top" style={{ minHeight: '500px' }}>
                    <div className="d-flex align-items-center mb-3">
                        <a href="/survey/template/list/" className="nav-link">
                            <span className="text-green fw-bold me-2 p-0">
                                <FontAwesomeIcon icon={solid('arrow-left')} /> Voltar à lista
                            </span>
                        </a>
                    </div>

                    <div className="bg-white rounded border-clean p-4" style={{ minHeight: '500px', marginBottom: '200px' }}>
                        {company?.id && !canCreateTemplate ? (
                            <div className="alert alert-warning">Você não tem permissão para criar templates.</div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Nome *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={form.name}
                                        onChange={e => setForm({ ...form, name: e.target.value })}
                                        placeholder="Ex: Clima Organizacional"
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Descrição</label>
                                    <Editor
                                        style={{ height: '180px' }}
                                        className="survey-description-editor"
                                        headerTemplate={headerEditor}
                                        value={form.description}
                                        onLoad={onSurveyDescriptionEditorLoad}
                                        onTextChange={(e) => setForm({ ...form, description: e.htmlValue ?? '' })}
                                    />
                                </div>
                                <div className="d-flex gap-2">
                                    <button type="submit" className="btn btn-success btn-sm btn-rounded px-3" disabled={submitting}>
                                        {submitting ? 'Criando...' : 'Criar Template'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </ProtectedRoute>
    );
}
